import { supabase } from "@/lib/supabase";

type CanonicalTransactionTable =
  | "personal_incomes"
  | "personal_expenses_fixed"
  | "personal_expenses_variable";

type CommandResult<T = Record<string, unknown>> = {
  data: T | null;
  error: { message: string; code?: string } | null;
};

function commandKey(scope: string) {
  return `client:${scope}:${crypto.randomUUID()}`;
}

function withoutClientOwnership(values: Record<string, unknown>) {
  const canonical = { ...values };
  delete canonical.user_id;
  delete canonical.id;
  delete canonical.created_at;
  delete canonical.updated_at;
  delete canonical.is_archived;
  return canonical;
}

async function rpc<T>(name: string, parameters: Record<string, unknown>) {
  const result = await (supabase as any).rpc(name, parameters);
  return result as CommandResult<T>;
}

export const CanonicalFinancialCommandService = {
  applyTransaction(input: {
    operation: "create" | "update" | "delete";
    sourceTable: CanonicalTransactionTable;
    sourceId?: string | null;
    values?: Record<string, unknown>;
    idempotencyKey?: string;
    correlationId?: string | null;
  }) {
    return rpc("apply_personal_transaction_command", {
      p_operation: input.operation,
      p_source_table: input.sourceTable,
      p_source_id: input.sourceId ?? null,
      p_values: withoutClientOwnership(input.values ?? {}),
      p_idempotency_key:
        input.idempotencyKey ?? commandKey(`transaction:${input.operation}`),
      p_correlation_id: input.correlationId ?? null,
    });
  },

  applyAccount(input: {
    operation: "create" | "update" | "archive";
    accountId?: string | null;
    values?: Record<string, unknown>;
    idempotencyKey?: string;
    correlationId?: string | null;
  }) {
    return rpc("apply_personal_account_command", {
      p_operation: input.operation,
      p_account_id: input.accountId ?? null,
      p_values: withoutClientOwnership(input.values ?? {}),
      p_idempotency_key:
        input.idempotencyKey ?? commandKey(`account:${input.operation}`),
      p_correlation_id: input.correlationId ?? null,
    });
  },

  createMovement(input: {
    accountId: string;
    type: "income" | "expense" | "adjustment";
    amount: number;
    description: string;
    category: string;
    transactionDate: string;
    transactionAt?: string | null;
    idempotencyKey?: string;
    correlationId?: string | null;
  }) {
    return rpc("create_personal_account_movement_command", {
      p_account_id: input.accountId,
      p_type: input.type,
      p_amount: input.amount,
      p_description: input.description,
      p_category: input.category,
      p_transaction_date: input.transactionDate,
      p_idempotency_key: input.idempotencyKey ?? commandKey("movement:create"),
      p_transaction_at: input.transactionAt ?? null,
      p_correlation_id: input.correlationId ?? null,
    });
  },

  createTransfer(input: {
    sourceAccountId: string;
    destinationAccountId: string;
    amount: number;
    description: string;
    transactionDate: string;
    transferId?: string;
    correlationId?: string;
    idempotencyKey?: string;
  }) {
    return rpc("create_personal_account_transfer_command", {
      p_source_account_id: input.sourceAccountId,
      p_destination_account_id: input.destinationAccountId,
      p_amount: input.amount,
      p_description: input.description,
      p_transaction_date: input.transactionDate,
      p_idempotency_key: input.idempotencyKey ?? commandKey("transfer:create"),
      p_transfer_id: input.transferId ?? crypto.randomUUID(),
      p_correlation_id: input.correlationId ?? crypto.randomUUID(),
    });
  },
};
